import type { FunctionComponent } from "react";
import { useEffect, useState, useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";

import {
  StyledContainer,
  StyledSelectedWrapper,
  StyledMainOptions,
} from "./styles";
import type { UpdateValueType } from "./components/UpdateValueOption";
import { UpdateValueOption } from "./components/UpdateValueOption";
import { MathOperations } from "./components/MathOperations";
import type { MathOptions } from "./components/MathOption";
import { MathOption } from "./components/MathOption";
import type {
  InputConfig_OperatorConfig,
  OperatorDescriptions_OperatorDescription,
} from "@smartsuite/automation-engine-api";
import {
  MathOperatorDescriptionKinds,
  OperatorDescriptionKinds,
} from "./constanst";
import { isAvailableOperator, isMathOption } from "./utils";
import { DropdownPanel, Icon } from "@smartsuite/react-ui/lib";
import { useRootStore } from "../../../../../../../../stores/hooks";

interface UpdateValueDropdownProps {
  onOpenedChange: (opened: boolean) => void;
  onChange: (value: UpdateValueType | MathOptions) => void;
  operatorConfig?: InputConfig_OperatorConfig;
  operatorDescriptions?: OperatorDescriptions_OperatorDescription[];
  inCaptionBlock?: boolean;
  disabled?: boolean;
}

const isMathOperatorDescriptionKinds = (
  operatorDescription: string,
): operatorDescription is MathOperatorDescriptionKinds => {
  return Object.values(MathOperatorDescriptionKinds).includes(
    operatorDescription as MathOperatorDescriptionKinds,
  );
};

export const UpdateValueDropdown: FunctionComponent<UpdateValueDropdownProps> =
  observer(
    ({
      onOpenedChange,
      onChange,
      operatorDescriptions,
      operatorConfig,
      inCaptionBlock,
      disabled = false,
    }) => {
      const [isOpened, setIsOpened] = useState(false);
      const wrapperRef = useRef();
      const [selectedValue, setSelectedValue] = useState<
        Partial<UpdateValueType | MathOptions>
      >(OperatorDescriptionKinds.REPLACE);

      const store = useRootStore();
      // Math operators are not offered inside a loop. "Inside" means the loop
      // ENCLOSING the open action (an action in a Conditional Logic branch inside
      // a loop counts), which the nearest-container locator does not tell.
      const isInLoop =
        store.automations.candidate.currentLoopScope !== undefined;

      useEffect(() => {
        if (operatorConfig) {
          const operatorKind = operatorConfig.operator.oneofKind;
          if (operatorKind === undefined) {
            setSelectedValue("replace" as UpdateValueType | MathOptions);
          } else {
            setSelectedValue(operatorKind as UpdateValueType | MathOptions);
          }
        }
      }, []);
      const isValueOption = (
        selected: UpdateValueType | MathOptions,
      ): selected is UpdateValueType =>
        [
          OperatorDescriptionKinds.REPLACE,
          OperatorDescriptionKinds.APPEND,
          OperatorDescriptionKinds.CLEAR,
        ].includes(selected);

      const handleToggle = useCallback(
        (e) => {
          e.stopPropagation();
          if (disabled) return;
          setIsOpened((prev) => {
            onOpenedChange(!prev);
            return !prev;
          });
        },
        [onOpenedChange, disabled],
      );

      const handleClickOutside = useCallback(() => {
        setIsOpened(false);
        onOpenedChange(false);
      }, [onOpenedChange]);

      const handleOnChange = (
        newValue: UpdateValueType | MathOptions,
      ): void => {
        if (disabled) return;
        setIsOpened(false);
        setSelectedValue(newValue);
        onChange(newValue);
      };

      const isAvailableMathOperator = useCallback((): boolean => {
        if (operatorDescriptions === undefined || isInLoop) {
          return false;
        }
        return operatorDescriptions.some((operatorDescription) => {
          return isMathOperatorDescriptionKinds(
            operatorDescription.operator.oneofKind ?? "",
          );
        });
      }, [operatorDescriptions, isInLoop]);

      return (
        <>
          <StyledSelectedWrapper
            isOpened={isOpened}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            ref={wrapperRef}
            onClick={handleToggle}
            isInCaptionBlock={inCaptionBlock}
            isDisabled={disabled}
          >
            {isValueOption(selectedValue) && (
              <UpdateValueOption
                type={selectedValue}
                isSelected={false}
                isPlaceholder={true}
              />
            )}
            {isMathOption(selectedValue) && (
              <MathOption isPlaceholder={true} type={selectedValue} />
            )}
            {!disabled && (
              <Icon
                className="dropdown-arrow"
                transition={true}
                name="arrow-down-small"
              />
            )}
          </StyledSelectedWrapper>
          {isOpened && (
            <DropdownPanel
              my="left top"
              at="left bottom+4"
              dropdownMaxWidth={250}
              minWidth={250}
              targetElementRef={wrapperRef}
              isActive={isOpened}
              onClickOutside={handleClickOutside}
            >
              <StyledContainer>
                <StyledMainOptions>
                  {isAvailableOperator(
                    OperatorDescriptionKinds.REPLACE,
                    operatorDescriptions,
                  ) && (
                    <UpdateValueOption
                      type={OperatorDescriptionKinds.REPLACE}
                      isSelected={
                        selectedValue === OperatorDescriptionKinds.REPLACE
                      }
                      onClick={handleOnChange}
                    />
                  )}
                  {isAvailableOperator(
                    OperatorDescriptionKinds.APPEND,
                    operatorDescriptions,
                  ) && (
                    <UpdateValueOption
                      type={OperatorDescriptionKinds.APPEND}
                      isSelected={
                        selectedValue === OperatorDescriptionKinds.APPEND
                      }
                      onClick={handleOnChange}
                    />
                  )}
                  {isAvailableOperator(
                    OperatorDescriptionKinds.CLEAR,
                    operatorDescriptions,
                  ) && (
                    <UpdateValueOption
                      type={OperatorDescriptionKinds.CLEAR}
                      isSelected={
                        selectedValue === OperatorDescriptionKinds.CLEAR
                      }
                      onClick={handleOnChange}
                    />
                  )}
                </StyledMainOptions>
                {isAvailableMathOperator() && (
                  <MathOperations
                    operatorDescriptions={operatorDescriptions}
                    onChange={handleOnChange}
                  />
                )}
              </StyledContainer>
            </DropdownPanel>
          )}
        </>
      );
    },
  );
